import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, API, fmtUSD, getToken, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Handshake, UploadCloud, Loader2, FileText, Trash2, ExternalLink, Sparkles, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  "in-review":  "bg-amber-50 text-amber-800 border-amber-200",
  "countered":  "bg-blue-50 text-blue-800 border-blue-200",
  "signed":     "bg-emerald-50 text-emerald-800 border-emerald-200",
  "abandoned":  "bg-slate-100 text-slate-600 border-slate-200",
};

export default function Negotiation() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null); // { data, matches, fileName }

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/negotiations");
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this negotiation draft? This cannot be undone.")) return;
    try {
      await api.delete(`/negotiations/${id}`);
      toast.success("Draft deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onCreated = (id, reviewJobId) => {
    setUploadOpen(false);
    const qs = reviewJobId ? `?review_job=${reviewJobId}` : "";
    nav(`/negotiation/${id}${qs}`);
  };

  const onDuplicateDetected = ({ data, matches, fileName }) => {
    setUploadOpen(false);
    setDuplicateInfo({ data, matches, fileName });
  };

  const resolveDuplicate = async (action, parentId = null) => {
    if (action === "cancel") { setDuplicateInfo(null); return; }
    if (action === "open") {
      setDuplicateInfo(null);
      nav(`/negotiation/${parentId}`);
      return;
    }
    // action === "new-round" — create as a new round under parent
    const info = duplicateInfo;
    setDuplicateInfo(null);
    const tId = toast.loading("Creating new round…");
    try {
      const created = await api.post("/negotiations/from-extract", {
        data: info.data,
        source_filename: info.fileName,
        parent_negotiation_id: parentId,
      });
      toast.dismiss(tId);
      toast.success(`Round ${created.data?.draft?.round || ""} created.`);
      const reviewJob = created.data.contract_review_job_id;
      const qs = reviewJob ? `?review_job=${reviewJob}` : "";
      nav(`/negotiation/${created.data.id}${qs}`);
    } catch (e) {
      toast.dismiss(tId);
      toast.error(formatApiError(e));
    }
  };

  return (
    <AppShell
      title="Negotiation Worksheet"
      subtitle="Upload a sponsor's proposed CTA, then negotiate side-by-side with your benchmark percentiles."
      actions={
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#F05D23] hover:bg-[#d94e18]" data-testid="neg-new-btn">
              <UploadCloud className="w-4 h-4 mr-1.5" /> Upload Sponsor CTA
            </Button>
          </DialogTrigger>
          <UploadDraftDialog onCreated={onCreated} onDuplicateDetected={onDuplicateDetected} />
        </Dialog>
      }
    >
      <DuplicateDialog
        info={duplicateInfo}
        onResolve={resolveDuplicate}
      />
      <div className="bg-white border border-border rounded-md shadow-sm" data-testid="negotiation-list">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900">Active Drafts</h3>
            <p className="text-xs text-slate-500 mt-0.5">Each row is a sponsor-proposed CTA you&apos;re actively negotiating.</p>
          </div>
          <Badge variant="outline" className="bg-slate-50 text-slate-700">{rows.length} draft{rows.length === 1 ? "" : "s"}</Badge>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : rows.length === 0 ? (
          <EmptyState onUpload={() => setUploadOpen(true)} />
        ) : (
          <table className="w-full text-sm stryde-table">
            <thead>
              <tr>
                <th className="text-left px-4 py-2.5">Study</th>
                <th className="text-left px-4 py-2.5">Sponsor</th>
                <th className="text-left px-4 py-2.5">PI</th>
                <th className="text-right px-4 py-2.5">Sponsor $</th>
                <th className="text-right px-4 py-2.5">Stryde Counter $</th>
                <th className="text-right px-4 py-2.5">Δ</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Updated</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const t = r.totals?.grand_total || {};
                const delta = (t.delta || 0);
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`neg-row-${r.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-slate-900">{r.study_name || "—"}</div>
                      {r.protocol_number && <div className="text-[11px] text-slate-500">{r.protocol_number}</div>}
                    </td>
                    <td className="px-4 py-2.5">{r.sponsor || "—"}</td>
                    <td className="px-4 py-2.5">{r.pi_name || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono-fin">{fmtUSD(t.sponsor)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-fin">{fmtUSD(t.counter)}</td>
                    <td className={`px-4 py-2.5 text-right font-mono-fin ${delta > 0 ? "text-emerald-700" : delta < 0 ? "text-red-700" : "text-slate-500"}`}>
                      {delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${fmtUSD(delta)}`}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={STATUS_COLORS[r.status] || STATUS_COLORS["in-review"]}>{r.status || "in-review"}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{(r.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => nav(`/negotiation/${r.id}`)} data-testid={`neg-open-${r.id}`}>
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`neg-del-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}

function EmptyState({ onUpload }) {
  return (
    <div className="p-12 text-center">
      <Handshake className="w-12 h-12 mx-auto text-slate-300" />
      <h4 className="mt-3 font-semibold text-slate-900">No negotiation drafts yet</h4>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        Drop a sponsor-proposed CTA (PDF, Word, or Excel) and we&apos;ll extract every line item plus pull P75/P80/P90 benchmark percentiles inline.
      </p>
      <Button className="mt-5 bg-[#F05D23] hover:bg-[#d94e18]" onClick={onUpload} data-testid="neg-empty-upload">
        <UploadCloud className="w-4 h-4 mr-1.5" /> Upload Sponsor CTA
      </Button>
    </div>
  );
}

/* ============================================================
 * UploadDraftDialog — re-uses the async polling extract endpoint,
 * then POSTs to /negotiations/from-extract.
 * ============================================================ */
function UploadDraftDialog({ onCreated, onDuplicateDetected }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const inputRef = useRef();

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const reset = () => { setFile(null); setBusy(false); setProgress(""); };

  const run = async () => {
    if (!file) return toast.error("Pick a file first.");
    setBusy(true);
    setProgress("Uploading…");
    const tId = toast.loading(`AI extracting ${file.name} — this can take 30–120s.`);
    try {
      // 1. Kick off async extract job
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/ctas/extract-document-async`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!r.ok) {
        let detail = `HTTP ${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* */ }
        throw new Error(detail);
      }
      const { job_id } = await r.json();
      if (!job_id) throw new Error("No job_id returned");
      setProgress("Extracting…");

      // 2. Poll for completion
      const POLL_MS = 2500;
      const MAX = 120;
      let data = null;
      let consec = 0;
      for (let i = 0; i < MAX; i++) {
        await new Promise((res) => setTimeout(res, POLL_MS));
        let jr;
        try {
          jr = await fetch(`${API}/ctas/extract-jobs/${job_id}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
        } catch {
          consec += 1;
          if (consec >= 8) throw new Error("Network unstable while polling — try again.");
          continue;
        }
        if (jr.ok) {
          consec = 0;
          const job = await jr.json();
          if (job.status === "done") { data = job.data; break; }
          if (job.status === "error") throw new Error(job.error || "Extraction failed");
          setProgress(`Extracting… (${i + 1})`);
        } else if (jr.status === 404) {
          throw new Error("Job expired before completion.");
        } else {
          consec += 1;
          if (consec >= 8) throw new Error(`Gateway unstable: ${jr.status}`);
        }
      }
      if (!data) throw new Error("Extraction timed out.");

      // 3. Check for duplicate study
      setProgress("Checking for duplicates…");
      let parentNegotiationId = null;
      try {
        const dupCheck = await api.post("/negotiations/check-duplicate", {
          sponsor: data.sponsor,
          study_name: data.study_name,
          protocol_number: data.protocol_number,
        });
        const matches = dupCheck.data?.matches || [];
        if (matches.length > 0) {
          toast.dismiss(tId);
          setBusy(false);
          // Show duplicate prompt — store data + matches so user can decide
          onDuplicateDetected({ data, matches, fileName: file.name });
          return;
        }
      } catch {
        // non-fatal; proceed
      }

      // 4. Create negotiation draft from extract
      setProgress("Saving draft…");
      const created = await api.post("/negotiations/from-extract", {
        data,
        source_filename: file.name,
        parent_negotiation_id: parentNegotiationId,
      });
      toast.dismiss(tId);
      const reviewJob = created.data.contract_review_job_id;
      toast.success(
        reviewJob
          ? "Draft created. Contract Language Review is running in the background."
          : "Draft created. Edit Stryde Counter values next."
      );
      reset();
      onCreated(created.data.id, reviewJob);
    } catch (err) {
      toast.dismiss(tId);
      toast.error(formatApiError(err) || err.message, { duration: 12000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="max-w-xl" data-testid="upload-draft-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#F05D23]" />
          New Negotiation — Upload Sponsor&apos;s Proposed CTA
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <div
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
            file ? "border-emerald-400 bg-emerald-50/40" : "border-slate-300 bg-white hover:bg-slate-50"
          }`}
          data-testid="neg-upload-dropzone"
        >
          {file ? (
            <div className="space-y-1">
              <FileText className="w-7 h-7 mx-auto text-emerald-600" />
              <div className="text-sm font-semibold text-slate-900 truncate">{file.name}</div>
              <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
              <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-xs text-red-600 hover:underline">Remove</button>
            </div>
          ) : (
            <div className="space-y-1">
              <UploadCloud className="w-8 h-8 mx-auto text-slate-400" />
              <div className="text-sm font-semibold text-slate-700">Click to upload sponsor&apos;s draft CTA</div>
              <div className="text-xs text-slate-400">.pdf / .docx / .xlsx / .csv / .txt</div>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv,.pdf,.docx,.txt"
            onChange={pickFile}
            className="hidden"
            data-testid="neg-upload-input"
          />
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex gap-2 text-[12px] text-amber-900">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Negotiation tab</strong> is for sponsor-proposed drafts. Final/signed CTAs should be uploaded via the <strong>CTA Library</strong> tab so they feed the benchmark.
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-slate-500">{busy ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" />{progress}</span> : null}</div>
          <Button onClick={run} disabled={!file || busy} className="bg-[#F05D23] hover:bg-[#d94e18]" data-testid="neg-upload-start">
            {busy ? "Extracting…" : "Extract & Start Negotiating"}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}

/* =========================================================
 * DuplicateDialog — shown when an upload matches an existing
 * negotiation by (sponsor + study_name) or protocol_number.
 * ========================================================= */
function DuplicateDialog({ info, onResolve }) {
  const open = !!info;
  const matches = info?.matches || [];
  const newestParent = matches[0]; // sorted desc by created_at on backend

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onResolve("cancel"); }}>
      <DialogContent className="max-w-lg" data-testid="dup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" /> Existing negotiation found
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-700">
            We already have {matches.length} negotiation{matches.length === 1 ? "" : "s"} for this study.
            How do you want to handle this upload?
          </p>
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-48 overflow-auto bg-slate-50/30">
            {matches.map((m) => (
              <div key={m.id} className="px-3 py-2 text-xs flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">
                    {m.study_name} {m.round > 1 ? <span className="text-slate-500 font-normal">· Round {m.round}</span> : null}
                  </div>
                  <div className="text-slate-500">
                    {m.sponsor}{m.protocol_number ? ` · ${m.protocol_number}` : ""} · {(m.created_at || "").slice(0, 10)} · {m.status || "in-review"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onResolve("cancel")}>Cancel upload</Button>
          <Button variant="outline" onClick={() => onResolve("open", newestParent.id)} data-testid="dup-open-existing">
            Open existing
          </Button>
          <Button
            onClick={() => onResolve("new-round", newestParent.id)}
            className="bg-[#0A4C95] hover:bg-[#083d79]"
            data-testid="dup-new-round"
          >
            Create new round
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
